import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { Component, ElementRef, forwardRef, Input, Output, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFabSliderComponent),
            multi: true,
        },
    ],
    selector: 'se-fab-slider',
    templateUrl: './se-fab-slider.component.html',
    styleUrls: ['./se-fab-slider.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatIcon, MatSlider, MatButton, FormsModule, MatSliderThumb, CommonModule],
    standalone: true,
})
export class SeFabSliderComponent implements ControlValueAccessor {
    @Input()
    value!: number;

    @Input()
    min: NumberInput;

    @Input()
    max: NumberInput;

    @Input()
    step: NumberInput;

    @Input()
    labelFactor: NumberInput = 1;

    @Input()
    buttonsVisible?: boolean = true;

    @Input()
    showLabel?: boolean = true;

    @Input()
    increaseIcon: string = 'add';
    @Input()
    decreaseIcon: string = 'remove';

    constructor(private elementRef: ElementRef) {}

    @Output()
    valueChange = (_: any) => {};

    formatLabel = (value: number): string => {
        return this.toFixed(value * coerceNumberProperty(this.labelFactor), 1).toString();
    };

    writeValue(value: number): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.valueChange = fn;
    }

    registerOnTouched(_: any): void {}

    setDisabledState?(_: boolean): void {}

    onInputChange(event: number) {
        this.value = event;
        this.valueChange(this.value);
    }

    onIncrease() {
        let value = this.toFixed(parseFloat(this.value.toString()), 5);
        let max = this.toFixed(parseFloat((this.max ?? 0).toString()), 5);
        let step = this.toFixed(parseFloat((this.step ?? 0).toString()), 5);

        if (value + step < max) this.onInputChange(value + step);
        else this.onInputChange(max);
    }

    onDecrease() {
        let value = this.toFixed(parseFloat(this.value.toString()), 5);
        let min = this.toFixed(parseFloat((this.min ?? 0).toString()), 5);
        let step = this.toFixed(parseFloat((this.step ?? 0).toString()), 5);

        if (value - step > min) this.onInputChange(value - step);
        else this.onInputChange(min);
    }

    canDecrease(): boolean {
        let value = this.value ? this.toFixed(parseFloat(this.value.toString()), 5) : 0;
        let min = this.toFixed(parseFloat((this.min ?? 0).toString()), 5);
        let step = this.toFixed(parseFloat((this.step ?? 0).toString()), 5);

        return value - step < min;
    }

    canIncrease(): boolean {
        let value = this.value ? this.toFixed(parseFloat(this.value.toString()), 5) : 0;
        let max = this.toFixed(parseFloat((this.max ?? 0).toString()), 5);
        let step = this.toFixed(parseFloat((this.step ?? 0).toString()), 5);

        return value + step > max;
    }

    private toFixed(value: number, precision: number) {
        const power = Math.pow(10, precision || 0);
        return Math.round(value * power) / power;
    }
}
